/**
 * PATH: packages/ui/src/kanban/components/card-detail/drawer-sections/cardTabLayout.ts
 * FILE_ID: UI-KANBAN-CARD-TAB-LAYOUT-001
 * PURPOSE: Pure order/hide prefs for the five Card-tab blocks the operator
 *   asked to rearrange: Action, Team chat, Card notes, Email, Description.
 * WHY: Those blocks were a hardcoded stack. Operators want Team chat or
 *   Card notes first on some boards, and to hide Email when it is noise.
 *   This module is the contract; the hook persists it; the menu edits it.
 * ROLE IN ARCHITECTURE: Kanban card-drawer layout preference (pure I→O).
 * MAIN EXPORTS:
 *   - CARD_TAB_BLOCK_IDS / DEFAULT_CARD_TAB_ORDER
 *   - coerceCardTabLayout, visibleCardTabBlocks
 *   - moveCardTabBlock, setCardTabBlockHidden
 *   - resetCardTabLayout(current?, boardId?) — no boardId resets global
 *     order/hidden and keeps boardOverrides; boardId deletes that override only
 * NON-RESPONSIBILITIES: Does not persist, render, or hide Email/Files tabs.
 * NOTES FOR FUTURE AI: Default order is a product contract. Adding a sixth
 *   block: append to CARD_TAB_BLOCK_IDS and give it a label. Coerce places
 *   unknown-missing ids at the end so old prefs keep working.
 */
export const CARD_TAB_BLOCK_IDS = [
  'find',
  'action',
  'files',
  'teamChat',
  'cardNotes',
  'email',
  'merged',
  'description',
  'workbook',
  'entries',
  'trello',
  'properties',
  'placements',
  'quote',
  'schedule',
  'softmatch',
  'subchains',
  'cashflow',
  'blockers',
  'restricted',
  'members',
  'labels',
  'checklists',
  'subcards',
  'linked',
  'activity',
] as const

export type CardTabBlockId = (typeof CARD_TAB_BLOCK_IDS)[number]

export const DEFAULT_CARD_TAB_ORDER: readonly CardTabBlockId[] = CARD_TAB_BLOCK_IDS

export const CARD_TAB_BLOCK_LABELS: Record<CardTabBlockId, string> = {
  find: 'Find in card',
  action: 'Action',
  files: 'Files',
  teamChat: 'Team chat',
  cardNotes: 'Card notes',
  email: 'Email',
  merged: 'Merged in',
  description: 'Description',
  workbook: 'Workbook',
  entries: 'Entries',
  trello: 'Trello mirror',
  properties: 'Properties',
  placements: 'Boards & lanes',
  quote: 'Quote won / lost',
  schedule: 'Schedule drift',
  softmatch: 'Similar cards',
  subchains: 'Sub-chains',
  cashflow: 'Cash flow',
  blockers: 'Blockers',
  restricted: 'Restricted access',
  members: 'Members',
  labels: 'Labels',
  checklists: 'Checklists',
  subcards: 'Sub-cards',
  linked: 'Linked',
  activity: 'Activity',
}

const CARD_TAB_BLOCK_CATEGORY: Record<CardTabBlockId, string> = {
  find: 'Work',
  action: 'Work',
  description: 'Work',
  checklists: 'Work',
  files: 'Documents',
  email: 'Email',
  merged: 'Email',
  teamChat: 'People',
  cardNotes: 'People',
  members: 'People',
  labels: 'People',
  restricted: 'People',
  workbook: 'Records',
  entries: 'Records',
  trello: 'Records',
  properties: 'Records',
  placements: 'Records',
  quote: 'Money',
  schedule: 'Money',
  cashflow: 'Money',
  softmatch: 'Links',
  subchains: 'Links',
  blockers: 'Links',
  subcards: 'Links',
  linked: 'Links',
  activity: 'Activity',
}

/** Group label for the drawer settings list. Activity stays its own group. */
export function cardTabBlockCategory(id: CardTabBlockId): string {
  return CARD_TAB_BLOCK_CATEGORY[id]
}

export interface CardTabBoardOverride {
  order?: CardTabBlockId[]
  hidden?: CardTabBlockId[]
}

export interface CardTabLayout {
  /** Every known block, operator order. Hidden ids stay here so Show
   *  restores the last position instead of dumping them at the bottom. */
  order: CardTabBlockId[]
  hidden: CardTabBlockId[]
  /** This-board order/hide. Missing board id uses the all-boards lists. */
  boardOverrides: Record<string, CardTabBoardOverride>
}

export const DEFAULT_CARD_TAB_LAYOUT: CardTabLayout = {
  order: [...DEFAULT_CARD_TAB_ORDER],
  hidden: [],
  boardOverrides: {},
}

export const CARD_TAB_LAYOUT_SCOPE = 'kanban:card-tab-layout'

function isBlockId(value: unknown): value is CardTabBlockId {
  return (
    typeof value === 'string' &&
    (CARD_TAB_BLOCK_IDS as readonly string[]).includes(value)
  )
}

/** Coerce an untrusted blob. Unknown ids drop; missing ids append in
 *  default order so a future block appears without wiping prefs. */
export function coerceCardTabLayout(raw: unknown): CardTabLayout {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_CARD_TAB_LAYOUT, order: [...DEFAULT_CARD_TAB_ORDER] }
  const rawOrder = (raw as { order?: unknown }).order
  const rawHidden = (raw as { hidden?: unknown }).hidden
  const seen = new Set<CardTabBlockId>()
  const order: CardTabBlockId[] = []
  if (Array.isArray(rawOrder)) {
    for (const item of rawOrder) {
      if (isBlockId(item) && !seen.has(item)) {
        seen.add(item)
        order.push(item)
      }
    }
  }
  for (const id of CARD_TAB_BLOCK_IDS) {
    if (!seen.has(id)) order.push(id)
  }
  const hidden: CardTabBlockId[] = []
  const hiddenSeen = new Set<CardTabBlockId>()
  if (Array.isArray(rawHidden)) {
    for (const item of rawHidden) {
      if (isBlockId(item) && !hiddenSeen.has(item)) {
        hiddenSeen.add(item)
        hidden.push(item)
      }
    }
  }
  const boardOverrides: Record<string, CardTabBoardOverride> = {}
  const rawOverrides = (raw as { boardOverrides?: unknown }).boardOverrides
  if (rawOverrides && typeof rawOverrides === 'object') {
    for (const [boardId, value] of Object.entries(
      rawOverrides as Record<string, unknown>,
    )) {
      if (!boardId || !value || typeof value !== 'object') continue
      const nested = coerceCardTabLayout({
        ...(value as object),
        boardOverrides: {},
      })
      boardOverrides[boardId] = { order: nested.order, hidden: nested.hidden }
    }
  }
  return { order, hidden, boardOverrides }
}

export function resolveCardTabLayout(
  layout: CardTabLayout,
  boardId?: string,
): CardTabLayout {
  if (!boardId) return layout
  const override = layout.boardOverrides[boardId]
  if (!override) return layout
  return {
    ...layout,
    order: override.order ?? layout.order,
    hidden: override.hidden ?? layout.hidden,
  }
}

export function patchCardTabBoard(
  layout: CardTabLayout,
  boardId: string,
  patch: CardTabBoardOverride,
): CardTabLayout {
  const prev = layout.boardOverrides[boardId] ?? {}
  return {
    ...layout,
    boardOverrides: {
      ...layout.boardOverrides,
      [boardId]: {
        order: patch.order ?? prev.order,
        hidden: patch.hidden ?? prev.hidden,
      },
    },
  }
}

export function visibleCardTabBlocks(layout: CardTabLayout): CardTabBlockId[] {
  const hidden = new Set(layout.hidden)
  return layout.order.filter((id) => !hidden.has(id))
}

export function moveCardTabBlock(
  layout: CardTabLayout,
  id: CardTabBlockId,
  direction: 'up' | 'down',
): CardTabLayout {
  const order = [...layout.order]
  const index = order.indexOf(id)
  if (index < 0) return layout
  const next = direction === 'up' ? index - 1 : index + 1
  if (next < 0 || next >= order.length) return layout
  const swap = order[next]
  order[next] = id
  order[index] = swap
  return { ...layout, order }
}

export function setCardTabBlockHidden(
  layout: CardTabLayout,
  id: CardTabBlockId,
  hidden: boolean,
): CardTabLayout {
  const current = new Set(layout.hidden)
  if (hidden) current.add(id)
  else current.delete(id)
  return { ...layout, hidden: CARD_TAB_BLOCK_IDS.filter((block) => current.has(block)) }
}

export function resetCardTabLayout(
  current?: CardTabLayout,
  boardId?: string,
): CardTabLayout {
  if (boardId) {
    const base = current ?? DEFAULT_CARD_TAB_LAYOUT
    const rest = { ...base.boardOverrides }
    delete rest[boardId]
    return { ...base, boardOverrides: rest }
  }
  return {
    order: [...DEFAULT_CARD_TAB_ORDER],
    hidden: [],
    boardOverrides: current?.boardOverrides ?? {},
  }
}
